import { NgIf } from '@angular/common';
import { Component, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { CardComponent } from '@components/card/card.component';
import { ConsultationRequestAddressComponent } from '@components/consultation-request/consultation-request-address/consultation-request-address.component';
import { ConsultationRequestFormComponent } from '@components/consultation-request/consultation-request-form/consultation-request-form.component';
import { ConsultationRequestPatientInfoComponent } from '@components/consultation-request/consultation-request-patient-info/consultation-request-patient-info.component';
import { MedicalHistoryQuestionsComponent } from '@components/consultation-request/medical-history-questions/medical-history-questions.component';
import { Glp1UpgradeDosageUpsellComponent } from '@components/consultation-request/upsells/upgrade-dosage-upsell/glp1-upgrade-dosage-upsell/glp1-upgrade-dosage-upsell.component';
import { ConsultationRequestHeaderComponent } from '@components/consultation-request-header/consultation-request-header.component';
import { DynamicQuestionnaireFormComponent } from '@components/dynamic-forms/dynamic-questionnaire-form/dynamic-questionnaire-form.component';
import { ErrorNotificationComponent } from '@components/error-notification/error-notification.component';

@Component({
  selector: 'app-glp1-renewal-consultation-request-form',
  templateUrl: './glp1-renewal-consultation-request-form.component.html',
  styleUrl: './glp1-renewal-consultation-request-form.component.scss',
  encapsulation: ViewEncapsulation.None,
  imports: [
    NgIf,
    ErrorNotificationComponent,
    ConsultationRequestHeaderComponent,
    ConsultationRequestPatientInfoComponent,
    CardComponent,
    DynamicQuestionnaireFormComponent,
    MedicalHistoryQuestionsComponent,
    ConsultationRequestAddressComponent,
    Glp1UpgradeDosageUpsellComponent,
  ],
})
export class Glp1RenewalConsultationRequestFormComponent extends ConsultationRequestFormComponent {
  @ViewChild('upsellQuestionsForm', { static: false }) private upsellQuestionsForm: DynamicQuestionnaireFormComponent;
  @ViewChild('upgradeDosageUpsell', { static: false }) private upgradeDosageUpsell: Glp1UpgradeDosageUpsellComponent;

  /**
   * Gets the GLP-1 medication test ID from the order.
   * Searches for the first medication test in customer_tests, falling back to the first test in tests array.
   */
  public get glp1MedicationTestId(): number | undefined {
    const firstMedication = this.consultationRequestOrder?.customer_tests?.find(
      (customerTest) => customerTest.test?.is_medication,
    );

    return firstMedication?.test?.id || this.consultationRequestOrder?.tests?.[0]?.test_id;
  }

  /**
   * Gets the upsell questions form.
   */
  private get upsellForm(): FormGroup {
    return this.upsellQuestionsForm?.form;
  }

  /**
   * Gets the upgrade dosage upsell form.
   */
  private get upgradeDosageUpsellForm(): FormGroup {
    return this.upgradeDosageUpsell?.form;
  }

  /**
   * {@inheritDoc}
   * Adds touching for upsell form and upgrade dosage form.
   */
  protected override markAllFormControlsAsTouched(): void {
    super.markAllFormControlsAsTouched();
    this.upsellForm?.markAllAsTouched();
    this.upgradeDosageUpsellForm.markAllAsTouched();
  }

  /**
   * {@inheritDoc}
   * Adds check for dosage form validity.
   */
  protected override areFormsValid(): boolean {
    return super.areFormsValid() && !this.upsellForm?.invalid && !this.upgradeDosageUpsellForm?.invalid;
  }

  /**
   * {@inheritDoc}
   * Checks if the forms are touched, including the upsell and upgrade dosage forms.
   */
  protected override areFormsTouched(): boolean {
    return super.areFormsTouched() && this.upsellForm?.touched && this.upgradeDosageUpsellForm?.touched;
  }
}
